<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1100px"
    scrollable
    persistent
  >
    <v-card class="d-flex flex-column" style="max-height: 90vh">
      <v-card-title
        class="headline primary white--text d-flex justify-space-between align-center py-2"
      >
        <div class="d-flex align-center">
          <v-icon left dark>mdi-file-table-outline</v-icon>
          Tuo kirjanpitoraportti (Procountor)
        </div>
        <div class="d-flex align-center">
          <v-dialog v-model="showInstructionsDialog" max-width="580px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                small
                dark
                v-bind="attrs"
                v-on="on"
                class="mr-2 text-none"
              >
                <v-icon small left>mdi-help-circle-outline</v-icon>
                Procountor-ohje
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline blue-grey darken-3 white--text py-3">
                <v-icon left dark>mdi-information-outline</v-icon>
                Ohje raportin ottoon Procountorista
              </v-card-title>
              <v-card-text class="pt-4 black--text">
                <div class="text-subtitle-1 font-weight-bold mb-2 blue-grey--text text--darken-4">
                  Toimi Procountorissa seuraavasti:
                </div>
                <div class="mb-3 d-flex align-center flex-wrap">
                  <v-chip color="blue-grey lighten-4" text-color="blue-grey darken-4" class="font-weight-bold mr-1">
                    Raportointi
                  </v-chip>
                  <v-icon small class="mx-1" color="blue-grey darken-2">mdi-chevron-right</v-icon>
                  <v-chip color="blue-grey lighten-4" text-color="blue-grey darken-4" class="font-weight-bold mr-1">
                    Kirjanpitoraportit
                  </v-chip>
                </div>
                <v-card outlined class="pa-3 mb-3 grey lighten-5">
                  <div class="d-flex align-center mb-2">
                    <v-icon small color="blue-grey darken-3" class="mr-2">mdi-check-circle</v-icon>
                    <div>Raporttityyppi: <strong>Päiväkirja</strong></div>
                  </div>
                  <div class="d-flex align-center">
                    <v-icon small color="blue-grey darken-3" class="mr-2">mdi-check-circle</v-icon>
                    <div>Esitystapa: <strong>Viennettäin, dimensioinnit näkyvillä</strong></div>
                  </div>
                </v-card>
                <div class="d-flex align-center flex-wrap mb-2 font-weight-medium">
                  <v-icon small color="green darken-2" class="mr-1">mdi-arrow-right-bold</v-icon>
                  Paina <strong>Aja raportti</strong>
                  <v-icon small class="mx-1" color="grey darken-2">mdi-chevron-right</v-icon>
                  <v-chip x-small color="green darken-2" dark class="font-weight-bold mr-1">
                    Excel
                  </v-chip>
                  <span>(tai CSV)</span>
                </div>
                <div class="text-caption grey--text text--darken-2">
                  Lataa tiedosto ja valitse se tuontiin. Järjestelmä tukee sekä .xlsx- että .csv-muotoja.
                </div>
              </v-card-text>
              <v-card-actions class="px-4 pb-3">
                <v-spacer></v-spacer>
                <v-btn color="blue-grey darken-3" text @click="showInstructionsDialog = false">
                  Sulje
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn icon dark @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text class="pt-4 flex-grow-1" style="overflow-y: auto">
        <!-- Step 1: File selection -->
        <v-card outlined class="pa-4 mb-4" v-if="vouchers.length === 0">
          <div class="text-h6 mb-3">Valitse Procountor-raportti (.csv tai .xlsx)</div>

          <!-- Ohje-laatikko -->
          <v-card
            outlined
            class="blue-grey lighten-5 pa-4 mb-4"
            style="border: 1px solid #b0bec5 !important; border-left: 5px solid #455a64 !important;"
          >
            <div class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-4 mb-2 d-flex align-center">
              <v-icon left color="blue-grey darken-3">mdi-information-outline</v-icon>
              Ohje Procountor-raportin ottoon:
            </div>
            <div class="text-body-2 black--text pl-1">
              <div class="d-flex align-center flex-wrap font-weight-bold mb-2">
                <v-chip small color="blue-grey lighten-4" class="blue-grey--text text--darken-4 font-weight-bold mr-1">
                  1. Raportointi
                </v-chip>
                <v-icon small color="blue-grey darken-2" class="mx-1">mdi-chevron-right</v-icon>
                <v-chip small color="blue-grey lighten-4" class="blue-grey--text text--darken-4 font-weight-bold mr-1">
                  2. Kirjanpitoraportit
                </v-chip>
              </div>

              <div class="ml-2 mb-2">
                <div class="d-flex align-center mb-1">
                  <v-icon x-small color="blue-grey darken-3" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                  <span>Raporttityyppi: <strong class="black--text">Päiväkirja</strong></span>
                </div>
                <div class="d-flex align-center mb-1">
                  <v-icon x-small color="blue-grey darken-3" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                  <span>Esitystapa: <strong class="black--text">Viennettäin, dimensioinnit näkyvillä</strong></span>
                </div>
              </div>

              <div class="d-flex align-center flex-wrap mt-2 pt-2" style="border-top: 1px dashed rgba(0, 0, 0, 0.15)">
                <v-icon small color="green darken-2" class="mr-1">mdi-arrow-right-bold</v-icon>
                <span class="mr-2 font-weight-medium black--text">Paina <strong>Aja raportti</strong></span>
                <v-icon small color="grey darken-2" class="mx-1">mdi-chevron-right</v-icon>
                <v-chip x-small color="green darken-2" dark class="font-weight-bold mr-2">
                  Excel (.xlsx / .csv)
                </v-chip>
                <span class="text-caption grey--text text--darken-3">
                  (Lataa tiedosto ja valitse se alta)
                </span>
              </div>
            </div>
          </v-card>

          <div class="text-body-2 grey--text text--darken-1 mb-3">
            Tiedosto ryhmitellään automaattisesti
            <strong>Tositenumeron</strong> ja
            <strong>Tositelajin</strong> perusteella. Kirjanpidon oletustilejä
            ei lueta, vaan tositteet kohdistetaan halutuille budjettitileille.
          </div>

          <v-file-input
            v-model="csvFile"
            accept=".csv, text/csv, .xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            label="Valitse raporttitiedosto (.csv tai .xlsx)"
            prepend-icon="mdi-upload"
            outlined
            dense
            show-size
            :loading="parsing"
            @change="handleFileChange"
          ></v-file-input>

          <v-alert v-if="parseError" type="error" dense outlined class="mt-2">
            {{ parseError }}
          </v-alert>
        </v-card>

        <!-- Step 2: Vouchers Reconciliation List -->
        <div v-if="vouchers.length > 0">
          <!-- Toolbar & Filters -->
          <v-row dense align="center" class="mb-3">
            <v-col cols="12" md="6">
              <v-btn-toggle v-model="filterStatus" mandatory dense>
                <v-btn small value="all">
                  Kaikki ({{ vouchers.length }})
                </v-btn>
                <v-btn small value="pending" color="warning">
                  Odottaa ({{ pendingCount }})
                </v-btn>
                <v-btn small value="assigned" color="success">
                  Kohdistettu ({{ assignedCount }})
                </v-btn>
                <v-btn small value="declined" color="grey">
                  Hylätyt ({{ declinedCount }})
                </v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                placeholder="Etsi kumppanin, tositteen tai selitteen nimellä..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="d-flex justify-space-between align-center mb-2 flex-wrap">
            <span class="text-caption grey--text">
              Näytetään {{ filteredVouchers.length }} /
              {{ vouchers.length }} tositetta
            </span>

            <div class="d-flex align-center flex-wrap">
              <!-- 1-Click Decline OP Bank Statements Button -->
              <v-btn
                v-if="pendingOpBankVouchers.length > 0"
                small
                outlined
                color="error"
                class="mr-2 my-1"
                @click="declineAllOpBankStatements"
                title="Hylkää kaikki LÄNSI-SUOMEN OSUUSPANKKI 'Tiliote tositteena' -tositteet yhdellä painalluksella"
              >
                <v-icon small left>mdi-bank-remove</v-icon>
                Hylkää LÄNSI-SUOMEN OP tiliotteet ({{ pendingOpBankVouchers.length }} kpl)
              </v-btn>

              <v-btn
                v-else-if="declinedOpBankVouchers.length > 0"
                text
                x-small
                color="primary"
                class="mr-2 my-1"
                @click="restoreAllOpBankStatements"
                title="Palauta hylätyt LÄNSI-SUOMEN OSUUSPANKKI -tiliotteet takaisin käsittelyyn"
              >
                <v-icon x-small left>mdi-restore</v-icon>
                Palauta OP-tiliotteet ({{ declinedOpBankVouchers.length }} kpl)
              </v-btn>

              <v-btn
                text
                x-small
                color="primary"
                class="my-1"
                @click="autoSuggestAccounts"
                title="Ehdottaa tiliä samanlaisten aiempien toimittajien perusteella"
              >
                <v-icon x-small left>mdi-magic-staff</v-icon>
                Automaattikohdista tunnetut kumppanit
              </v-btn>
            </div>
          </div>

          <!-- Voucher cards -->
          <v-card
            v-for="v in filteredVouchers"
            :key="v.groupKey"
            outlined
            class="mb-3"
            :class="{
              'grey lighten-4': v.status === 'declined',
              'green lighten-5': v.status === 'assigned' && v.selectedAccountId,
              'yellow lighten-5': v.status === 'pending',
            }"
          >
            <v-card-text class="pa-3">
              <!-- Top Row: Voucher info -->
              <div
                class="d-flex flex-wrap justify-space-between align-center mb-2"
              >
                <div class="d-flex align-center flex-wrap">
                  <v-chip
                    small
                    :color="getVoucherTypeColor(v.tositelaji)"
                    dark
                    class="font-weight-bold mr-2 mb-1"
                  >
                    {{ v.tositelaji }}
                  </v-chip>
                  <span class="font-weight-bold text-subtitle-2 mr-3 mb-1">
                    #{{ v.tositenro }}
                  </span>
                  <span class="grey--text text--darken-2 mr-3 mb-1">
                    {{ v.tositepvm }}
                  </span>
                  <span class="font-weight-medium text-body-2 mr-3 mb-1">
                    {{ v.partnerName || "Tuntematon kumppani" }}
                  </span>
                </div>

                <div class="d-flex align-center">
                  <span
                    class="font-weight-bold text-subtitle-1 mr-3"
                    :class="
                      v.totalAmount < 0
                        ? 'red--text text--darken-2'
                        : 'green--text text--darken-2'
                    "
                  >
                    {{ formatCurrency(v.totalAmount) }}
                  </span>

                  <v-chip
                    x-small
                    :color="getStatusColor(v.status)"
                    text-color="white"
                  >
                    {{ getStatusLabel(v.status) }}
                  </v-chip>
                </div>
              </div>

              <v-divider class="my-2"></v-divider>

              <!-- Mode Header & Split Toggle -->
              <div v-if="v.status !== 'declined'" class="d-flex justify-space-between align-center my-2">
                <div class="text-caption font-weight-bold grey--text text--darken-2 d-flex align-center">
                  <v-icon small class="mr-1" :color="v.isSplitMode ? 'deep-purple' : 'primary'">
                    {{ v.isSplitMode ? 'mdi-call-split' : 'mdi-target' }}
                  </v-icon>
                  <span v-if="!v.isSplitMode">Kohdistus budjettiin:</span>
                  <span v-else class="deep-purple--text">
                    Jaettu useammalle kustannuspaikalle ({{ (v.splits || []).length }} osaa):
                  </span>
                </div>

                <v-btn
                  x-small
                  outlined
                  :color="v.isSplitMode ? 'primary' : 'deep-purple'"
                  @click="toggleSplitMode(v)"
                >
                  <v-icon x-small left>{{
                    v.isSplitMode ? 'mdi-arrow-collapse-all' : 'mdi-call-split'
                  }}</v-icon>
                  {{
                    v.isSplitMode ? 'Yhtenäinen kohdistus' : 'Jaa useammalle kustannuspaikalle'
                  }}
                </v-btn>
              </div>

              <!-- SINGLE MODE: Assignment Controls -->
              <div v-if="v.status !== 'declined' && !v.isSplitMode" class="my-2">
                <v-row dense align="center">
                  <v-col cols="12" sm="5">
                    <v-select
                      v-model="v.projectId"
                      :items="projectItems"
                      item-text="fullName"
                      item-value="id"
                      label="Kustannuspaikka"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-domain"
                      @change="onProjectChange(v)"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" sm="7">
                    <v-autocomplete
                      v-model="v.selectedAccountId"
                      :items="getAccountsForProject(v.projectId)"
                      item-text="displayName"
                      item-value="id"
                      label="Valitse budjettitili *"
                      placeholder="Valitse tili tai hae nimellä..."
                      outlined
                      dense
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-book-open-outline"
                      @change="onAccountSelected(v)"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>
                            <strong>{{ item.accountNumber }}</strong>
                            <span
                              v-if="item.subAccount"
                              class="grey--text text--darken-1 ml-1 font-mono text-caption"
                            >
                              ({{ item.subAccount }})
                            </span>
                            {{ item.accountName }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption">
                            {{ item.category }} | Arvio:
                            {{ formatCurrency(item.arvio) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </div>

              <!-- SPLIT MODE: Multi-Cost-Center / Account Controls -->
              <div
                v-if="v.status !== 'declined' && v.isSplitMode"
                class="my-2 pa-2 rounded deep-purple lighten-5 border"
              >
                <!-- Balance Status Bar -->
                <div class="d-flex justify-space-between align-center flex-wrap mb-2 pb-1 border-bottom">
                  <div class="text-caption">
                    Tositteen summa: <strong>{{ formatCurrency(Math.abs(v.totalAmount)) }}</strong>
                    <span class="mx-2 grey--text">|</span>
                    Jaettu:
                    <strong
                      :class="
                        isSplitBalanced(v)
                          ? 'green--text text--darken-2'
                          : 'red--text text--darken-2'
                      "
                    >
                      {{ formatCurrency(getSplitTotal(v)) }}
                    </strong>
                  </div>

                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      :color="isSplitBalanced(v) ? 'success' : 'error'"
                      text-color="white"
                      class="font-weight-medium"
                    >
                      <v-icon x-small left>{{
                        isSplitBalanced(v)
                          ? 'mdi-check-circle'
                          : 'mdi-alert-circle'
                      }}</v-icon>
                      {{
                        isSplitBalanced(v)
                          ? 'Loppusumma täsmää'
                          : `Erotus: ${formatCurrency(getSplitDiff(v))}`
                      }}
                    </v-chip>

                    <v-btn
                      v-if="!isSplitBalanced(v)"
                      x-small
                      text
                      color="primary"
                      class="ml-1"
                      @click="autoFillRemainingSplit(v)"
                    >
                      <v-icon x-small left>mdi-scale-balance</v-icon>
                      Tasaa loppusumma
                    </v-btn>
                  </div>
                </div>

                <!-- Split parts list -->
                <div
                  v-for="(sp, sIdx) in v.splits"
                  :key="sp.id || sIdx"
                  class="pa-2 mb-2 white rounded elevation-1"
                >
                  <div class="d-flex justify-space-between align-center mb-1">
                    <div class="text-caption font-weight-bold deep-purple--text">
                      Osa {{ sIdx + 1 }} / {{ v.splits.length }}
                    </div>
                    <v-btn
                      v-if="v.splits.length > 1"
                      icon
                      x-small
                      color="error"
                      @click="removeSplit(v, sIdx)"
                      title="Poista jako-osa"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>

                  <v-row dense align="center">
                    <v-col cols="12" sm="4" md="3">
                      <v-select
                        v-model="sp.projectId"
                        :items="projectItems"
                        item-text="fullName"
                        item-value="id"
                        label="Kustannuspaikka *"
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-domain"
                        @change="onSplitProjectChange(v, sp)"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="5" md="5">
                      <v-autocomplete
                        v-model="sp.selectedAccountId"
                        :items="getAccountsForProject(sp.projectId)"
                        item-text="displayName"
                        item-value="id"
                        label="Valitse budjettitili *"
                        placeholder="Valitse tili..."
                        outlined
                        dense
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-book-open-outline"
                        @change="onSplitAccountChange(v)"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <strong>{{ item.accountNumber }}</strong>
                              <span
                                v-if="item.subAccount"
                                class="grey--text text--darken-1 ml-1 font-mono text-caption"
                              >
                                ({{ item.subAccount }})
                              </span>
                              {{ item.accountName }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                              {{ item.category }} | Arvio:
                              {{ formatCurrency(item.arvio) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="3" md="2">
                      <v-text-field
                        v-model.number="sp.amount"
                        type="number"
                        step="0.01"
                        min="0"
                        label="Osuus (€) *"
                        outlined
                        dense
                        hide-details
                        suffix="€"
                        @input="onSplitAmountChange(v)"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="2">
                      <v-text-field
                        v-model="sp.description"
                        label="Kuvaus / Selite"
                        placeholder="Vientiselite..."
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Split bottom actions -->
                <div class="d-flex justify-space-between align-center mt-2">
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    @click="addSplit(v)"
                  >
                    <v-icon x-small left>mdi-plus</v-icon>
                    Lisää jako-osa
                  </v-btn>
                </div>
              </div>

              <!-- Declined state notice -->
              <div
                v-if="v.status === 'declined'"
                class="text-caption grey--text d-flex align-center my-1"
              >
                <v-icon small color="grey" class="mr-1">mdi-cancel</v-icon>
                Tosite on hylätty (ei viedä budjettiin).
                <span v-if="v.declineReason" class="ml-1 font-italic"
                  >({{ v.declineReason }})</span
                >
              </div>

              <!-- Itemized Vientiselite rows (Reference from CSV) -->
              <div
                v-if="v.items && v.items.length > 0"
                class="mt-2 pa-2 rounded grey lighten-4"
              >
                <div
                  class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                >
                  <span>Alkuperäiset kirjanpidon vientiselitteet ({{ v.items.length }} kpl):</span>
                </div>

                <div>
                  <div
                    v-for="(it, itIdx) in v.items"
                    :key="it.id || itIdx"
                    class="d-flex justify-space-between align-center text-body-2 py-1 border-bottom"
                  >
                    <div class="text-truncate mr-2" style="max-width: 75%">
                      <v-icon x-small color="grey">mdi-circle-small</v-icon>
                      <span class="font-weight-medium">{{
                        it.vientiselite || "(Ei vientiselitettä)"
                      }}</span>
                      <span
                        v-if="it.alvPercent"
                        class="text-caption grey--text ml-2"
                      >
                        (ALV {{ it.alvPercent }})
                      </span>
                    </div>
                    <span
                      class="font-weight-bold text-caption"
                      :class="
                        it.amount < 0
                          ? 'red--text text--darken-2'
                          : 'green--text text--darken-2'
                      "
                    >
                      {{ formatCurrency(it.amount) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action buttons per card -->
              <div class="d-flex justify-end align-center mt-2 flex-wrap">
                <v-btn
                  v-if="isOpBankStatement(v) && v.status !== 'declined' && pendingOpBankVouchers.length > 1"
                  text
                  x-small
                  color="error"
                  class="mr-2"
                  @click="declineAllOpBankStatements"
                  title="Hylkää kaikki LÄNSI-SUOMEN OSUUSPANKKI 'Tiliote tositteena' -tositteet"
                >
                  <v-icon x-small left>mdi-bank-remove</v-icon>
                  Hylkää kaikki OP-tiliotteet ({{ pendingOpBankVouchers.length }} kpl)
                </v-btn>

                <v-btn
                  v-if="v.status !== 'declined'"
                  text
                  x-small
                  color="error"
                  @click="declineVoucher(v)"
                >
                  <v-icon x-small left>mdi-close</v-icon> Hylkää / Ohita
                </v-btn>
                <v-btn
                  v-else
                  text
                  x-small
                  color="primary"
                  @click="restoreVoucher(v)"
                >
                  <v-icon x-small left>mdi-restore</v-icon> Palauta käsittelyyn
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Card Footer -->
      <v-card-actions class="pa-4 grey lighten-4">
        <v-btn
          v-if="vouchers.length > 0"
          text
          color="secondary"
          @click="resetVouchers"
        >
          Valitse toinen tiedosto
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)" :disabled="saving">
          Peruuta
        </v-btn>
        <v-btn
          v-if="vouchers.length > 0"
          color="primary"
          :loading="saving"
          :disabled="assignedCount === 0 && declinedCount === 0"
          @click="commitImport"
        >
          <v-icon left>mdi-content-save</v-icon>
          Tallenna tapahtumat ({{ assignedCount }} kohdistettu)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  readCsvFileAsText,
  parseAccountingCsv,
  COST_CENTERS,
} from "@/utils/budgetCsvParser";
import {
  saveTransactionsBatch,
  deleteTransaction,
} from "@/services/budgetService";

export default {
  name: "BudgetCsvImportDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    year: {
      type: Number,
      default: 2026,
    },
    projects: {
      type: Array,
      default: () => [],
    },
    existingTransactions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      csvFile: null,
      parsing: false,
      parseError: "",
      vouchers: [],
      filterStatus: "all",
      search: "",
      saving: false,
      showInstructionsDialog: false,
    };
  },
  computed: {
    projectItems() {
      return this.projects.map((p) => ({
        id: p.id || p.code,
        fullName: p.fullName || `${p.code} ${p.name}`,
      }));
    },
    pendingCount() {
      return this.vouchers.filter((v) => v.status === "pending").length;
    },
    assignedCount() {
      return this.vouchers.filter((v) => v.status === "assigned").length;
    },
    declinedCount() {
      return this.vouchers.filter((v) => v.status === "declined").length;
    },
    pendingOpBankVouchers() {
      return this.vouchers.filter(
        (v) => v.status !== "declined" && this.isOpBankStatement(v),
      );
    },
    declinedOpBankVouchers() {
      return this.vouchers.filter(
        (v) => v.status === "declined" && this.isOpBankStatement(v),
      );
    },
    filteredVouchers() {
      let list = this.vouchers;

      // Status filter
      if (this.filterStatus !== "all") {
        list = list.filter((v) => v.status === this.filterStatus);
      }

      // Search filter
      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        list = list.filter((v) => {
          return (
            (v.partnerName && v.partnerName.toLowerCase().includes(q)) ||
            (v.tositenro && String(v.tositenro).toLowerCase().includes(q)) ||
            (v.tositelaji && v.tositelaji.toLowerCase().includes(q)) ||
            (v.items &&
              v.items.some(
                (it) =>
                  it.vientiselite && it.vientiselite.toLowerCase().includes(q),
              ))
          );
        });
      }

      return list;
    },
  },
  watch: {
    value(val) {
      if (!val) {
        this.resetVouchers();
      }
    },
  },
  methods: {
    async handleFileChange(file) {
      this.parseError = "";
      this.vouchers = [];
      if (!file) return;

      this.parsing = true;
      try {
        const text = await readCsvFileAsText(file);
        const parsedVouchers = parseAccountingCsv(text);

        // Check against existing transactions to mark already imported
        const existingKeys = new Set(
          this.existingTransactions.map(
            (tx) => `${tx.tositelaji}_${tx.tositenro}`,
          ),
        );

        parsedVouchers.forEach((v) => {
          // Initialize split mode and splits
          this.$set(v, "isSplitMode", false);
          this.$set(v, "splits", []);

          if (existingKeys.has(v.groupKey)) {
            const matchingTxs = this.existingTransactions.filter(
              (tx) => `${tx.tositelaji}_${tx.tositenro}` === v.groupKey,
            );
            if (matchingTxs.length > 1) {
              // It was previously split across cost centers / accounts!
              this.$set(v, "isSplitMode", true);
              this.$set(
                v,
                "splits",
                matchingTxs.map((tx, idx) => ({
                  id: tx.id || `${v.id}_split_${idx + 1}`,
                  projectId: tx.projectId,
                  selectedAccountId: tx.accountId,
                  amount: Math.abs(tx.amount),
                  description:
                    (tx.items && tx.items[0] && tx.items[0].vientiselite) ||
                    tx.partnerName ||
                    "",
                })),
              );
              this.checkVoucherSplitStatus(v);
            } else if (matchingTxs.length === 1) {
              const match = matchingTxs[0];
              if (match.status === "declined") {
                v.status = "declined";
                v.declineReason = match.declineReason || "Aiemmin hylätty";
              } else {
                v.status = "assigned";
                v.projectId = match.projectId || v.projectId;
                v.selectedAccountId = match.accountId || "";
                this.onAccountSelected(v);
              }
            }
          } else {
            // Auto match project if not already assigned
            if (!v.projectId) {
              const defaultP = this.projects[0];
              v.projectId = defaultP ? defaultP.id : "10";
            }
          }
        });

        this.vouchers = parsedVouchers;
        this.filterStatus = this.pendingCount > 0 ? "pending" : "all";
      } catch (err) {
        console.error("CSV parse error:", err);
        this.parseError = err.message || "Tiedoston käsittely epäonnistui.";
      } finally {
        this.parsing = false;
      }
    },
    resetVouchers() {
      this.csvFile = null;
      this.vouchers = [];
      this.parseError = "";
      this.search = "";
      this.filterStatus = "all";
    },
    getAccountsForProject(projectId) {
      const proj = this.projects.find(
        (p) =>
          String(p.id) === String(projectId) ||
          String(p.code) === String(projectId),
      );
      if (!proj || !proj.accounts) return [];

      return proj.accounts
        .filter(
          (a) =>
            !a.isTotal &&
            !a.isTitle &&
            !a.isHeader &&
            (!a.accountNumber ||
              !/^\d{1,3}$/.test(String(a.accountNumber).trim())),
        )
        .map((a) => ({
          ...a,
          displayName: `${a.accountNumber ? a.accountNumber + " " : ""}${
            a.subAccount ? "(" + a.subAccount + ") " : ""
          }${a.accountName}`,
        }));
    },
    getAccountInProject(projectId, accountId) {
      if (!projectId || !accountId) return null;
      const accounts = this.getAccountsForProject(projectId);
      return accounts.find((a) => a.id === accountId) || null;
    },
    onProjectChange(voucher) {
      voucher.selectedAccountId = "";
      this.checkVoucherSplitStatus(voucher);
    },
    onAccountSelected(voucher) {
      if (voucher.selectedAccountId) {
        const acc = this.getAccountInProject(
          voucher.projectId,
          voucher.selectedAccountId,
        );
        if (acc) {
          voucher.selectedAccountName = acc.accountName;
          voucher.selectedAccountNumber = acc.accountNumber;
        }
      }
      this.checkVoucherSplitStatus(voucher);
    },
    toggleSplitMode(voucher) {
      voucher.isSplitMode = !voucher.isSplitMode;
      if (voucher.isSplitMode) {
        if (!voucher.splits || voucher.splits.length === 0) {
          this.initSplitsForVoucher(voucher);
        }
      }
      this.checkVoucherSplitStatus(voucher);
    },
    initSplitsForVoucher(voucher) {
      const total = Math.abs(voucher.totalAmount);
      const defaultProj =
        voucher.projectId ||
        (this.projects[0] && this.projects[0].id) ||
        "10";

      if (voucher.items && voucher.items.length > 1) {
        this.$set(
          voucher,
          "splits",
          voucher.items.map((it, idx) => ({
            id: `${voucher.id}_split_${idx + 1}`,
            projectId: defaultProj,
            selectedAccountId:
              it.selectedAccountId || voucher.selectedAccountId || "",
            amount: Math.abs(it.amount),
            description: it.vientiselite || `Rivi ${idx + 1}`,
          })),
        );
      } else {
        const half = Math.round((total / 2) * 100) / 100;
        const secondHalf = Math.round((total - half) * 100) / 100;
        const otherProj =
          (
            this.projects.find(
              (p) => String(p.id) !== String(defaultProj),
            ) || {}
          ).id || defaultProj;

        this.$set(voucher, "splits", [
          {
            id: `${voucher.id}_split_1`,
            projectId: defaultProj,
            selectedAccountId: voucher.selectedAccountId || "",
            amount: half,
            description:
              (voucher.items[0] && voucher.items[0].vientiselite) ||
              voucher.partnerName ||
              "",
          },
          {
            id: `${voucher.id}_split_2`,
            projectId: otherProj,
            selectedAccountId: "",
            amount: secondHalf,
            description:
              (voucher.items[0] && voucher.items[0].vientiselite) ||
              voucher.partnerName ||
              "",
          },
        ]);
      }
    },
    addSplit(voucher) {
      if (!voucher.splits) this.$set(voucher, "splits", []);
      const remaining = Math.max(0, this.getSplitDiff(voucher));
      const nextIdx = voucher.splits.length + 1;
      const defaultProj = (this.projects[0] && this.projects[0].id) || "10";
      voucher.splits.push({
        id: `${voucher.id}_split_${Date.now()}_${nextIdx}`,
        projectId: defaultProj,
        selectedAccountId: "",
        amount: remaining > 0 ? remaining : 0,
        description: "",
      });
      this.checkVoucherSplitStatus(voucher);
    },
    removeSplit(voucher, index) {
      if (voucher.splits && voucher.splits.length > 1) {
        voucher.splits.splice(index, 1);
        this.checkVoucherSplitStatus(voucher);
      }
    },
    onSplitProjectChange(voucher, split) {
      split.selectedAccountId = "";
      this.checkVoucherSplitStatus(voucher);
    },
    onSplitAccountChange(voucher) {
      this.checkVoucherSplitStatus(voucher);
    },
    onSplitAmountChange(voucher) {
      this.checkVoucherSplitStatus(voucher);
    },
    getSplitTotal(voucher) {
      if (!voucher.splits || voucher.splits.length === 0) return 0;
      const sum = voucher.splits.reduce(
        (acc, s) => acc + (parseFloat(s.amount) || 0),
        0,
      );
      return Math.round(sum * 100) / 100;
    },
    getSplitDiff(voucher) {
      const total = Math.abs(voucher.totalAmount);
      const splitTot = this.getSplitTotal(voucher);
      return Math.round((total - splitTot) * 100) / 100;
    },
    isSplitBalanced(voucher) {
      return Math.abs(this.getSplitDiff(voucher)) < 0.01;
    },
    autoFillRemainingSplit(voucher) {
      if (!voucher.splits || voucher.splits.length === 0) return;
      const total = Math.abs(voucher.totalAmount);
      const sumOthers = voucher.splits
        .slice(0, -1)
        .reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0);
      const lastAmount = Math.max(
        0,
        Math.round((total - sumOthers) * 100) / 100,
      );
      voucher.splits[voucher.splits.length - 1].amount = lastAmount;
      this.checkVoucherSplitStatus(voucher);
    },
    checkVoucherSplitStatus(voucher) {
      if (voucher.status === "declined") return;
      if (!voucher.isSplitMode) {
        voucher.status = voucher.selectedAccountId ? "assigned" : "pending";
        return;
      }
      const isBalanced = this.isSplitBalanced(voucher);
      const allAssigned =
        voucher.splits &&
        voucher.splits.length > 0 &&
        voucher.splits.every(
          (s) =>
            s.projectId &&
            s.selectedAccountId &&
            (parseFloat(s.amount) || 0) > 0,
        );
      voucher.status = isBalanced && allAssigned ? "assigned" : "pending";
    },
    declineVoucher(voucher) {
      voucher.status = "declined";
      voucher.declineReason = "Käyttäjän hylkäämä";
    },
    restoreVoucher(voucher) {
      voucher.declineReason = "";
      this.checkVoucherSplitStatus(voucher);
    },
    isOpBankStatement(v) {
      if (!v) return false;
      const pName = (v.partnerName || "").toLowerCase().replace(/[\s\-_]/g, "");
      const tLaji = (v.tositelaji || "").toLowerCase();

      const isOp =
        pName.includes("länsisuomenosuuspankki") ||
        pName.includes("lansisuomenosuuspankki") ||
        pName.includes("osuuspankki") ||
        pName.includes("op-palvelut") ||
        pName.includes("oppalvelut");

      const isTiliote =
        tLaji.includes("tiliote") ||
        tLaji === "tiliote tositteena";

      return isOp && isTiliote;
    },
    declineAllOpBankStatements() {
      let count = 0;
      this.vouchers.forEach((v) => {
        if (v.status !== "declined" && this.isOpBankStatement(v)) {
          v.status = "declined";
          v.declineReason = 'LÄNSI-SUOMEN OSUUSPANKKI "Tiliote tositteena"';
          count++;
        }
      });

      if (count > 0) {
        this.$toast?.info?.(
          `Hylätty ${count} kpl LÄNSI-SUOMEN OSUUSPANKKI "Tiliote tositteena" -tositteita.`,
        );
      } else {
        this.$toast?.info?.(
          "Ei löytynyt avoimia LÄNSI-SUOMEN OSUUSPANKKI -tiliotteita.",
        );
      }
    },
    restoreAllOpBankStatements() {
      let count = 0;
      this.vouchers.forEach((v) => {
        if (v.status === "declined" && this.isOpBankStatement(v)) {
          v.status = "pending";
          v.declineReason = "";
          this.checkVoucherSplitStatus(v);
          count++;
        }
      });

      if (count > 0) {
        this.$toast?.success?.(
          `Palautettu ${count} kpl LÄNSI-SUOMEN OSUUSPANKKI -tiliotteita käsittelyyn.`,
        );
      }
    },
    autoSuggestAccounts() {
      // Build a map of partnerName -> previously used account from existingTransactions
      const partnerAccountMap = new Map();
      this.existingTransactions.forEach((tx) => {
        if (tx.partnerName && tx.accountId && tx.status === "assigned") {
          partnerAccountMap.set(tx.partnerName.trim().toLowerCase(), {
            projectId: tx.projectId,
            accountId: tx.accountId,
          });
        }
      });

      let matchCount = 0;
      this.vouchers.forEach((v) => {
        if (v.status === "pending" && !v.isSplitMode && v.partnerName) {
          const key = v.partnerName.trim().toLowerCase();
          if (partnerAccountMap.has(key)) {
            const match = partnerAccountMap.get(key);
            v.projectId = match.projectId;
            v.selectedAccountId = match.accountId;
            this.onAccountSelected(v);
            matchCount++;
          }
        }
      });

      if (matchCount > 0) {
        this.$toast?.success?.(
          `Automaattikohdistettiin ${matchCount} tositetta!`,
        );
      } else {
        this.$toast?.info?.(
          "Ei löytynyt aiempia kohdistuksia näille kumppaneille.",
        );
      }
    },
    async commitImport() {
      this.saving = true;
      try {
        const transactionsToSave = [];
        const userContext = this.$userDBContext
          ? { ...this.$userDBContext }
          : {};

        this.vouchers.forEach((v) => {
          if (v.status === "assigned" || v.status === "declined") {
            const isVoucherExpense =
              v.tositelaji === "Ostolasku" ||
              v.tositelaji === "Kululasku" ||
              v.totalAmount < 0;

            const baseTx = {
              id: `${this.year}_${v.tositelaji}_${v.tositenro}`,
              year: parseInt(this.year, 10),
              tositelaji: v.tositelaji,
              tositenro: v.tositenro,
              tositepvm: v.tositepvm,
              partnerName: v.partnerName,
              originalProject: v.originalProject,
              projectId: v.projectId,
              status: v.status,
              declineReason: v.declineReason || "",
              amount: isVoucherExpense
                ? -Math.abs(v.totalAmount)
                : v.totalAmount, // Kulut miinusmerkkisenä
              importedAt: new Date().toISOString(),
              importedBy: userContext,
            };

            if (v.status === "assigned") {
              if (v.isSplitMode && v.splits && v.splits.length > 0) {
                v.splits.forEach((sp, spIdx) => {
                  const splitAmount =
                    Math.round(
                      Math.abs(parseFloat(sp.amount) || 0) * 100,
                    ) / 100;
                  const acc = this.getAccountInProject(
                    sp.projectId,
                    sp.selectedAccountId,
                  );
                  const isExpenseTarget =
                    (acc &&
                      (acc.type === "expense" ||
                        (acc.type === "investment" &&
                          acc.accountNumber &&
                          String(acc.accountNumber).startsWith("7")))) ||
                    isVoucherExpense;

                  const signedAmount = isExpenseTarget
                    ? -splitAmount
                    : splitAmount;

                  transactionsToSave.push({
                    ...baseTx,
                    id: `${baseTx.id}_split_${spIdx + 1}`,
                    projectId: sp.projectId,
                    accountId: sp.selectedAccountId,
                    accountName: acc ? acc.accountName : "",
                    accountNumber: acc ? acc.accountNumber : "",
                    subAccount: acc ? acc.subAccount || "" : "",
                    amount: signedAmount,
                    isSplit: true,
                    splitIndex: spIdx + 1,
                    splitCount: v.splits.length,
                    originalTotalAmount: isVoucherExpense
                      ? -Math.abs(v.totalAmount)
                      : v.totalAmount,
                    items: [
                      {
                        id: `${baseTx.id}_split_item_${spIdx + 1}`,
                        vientiselite:
                          sp.description ||
                          (v.items[0] && v.items[0].vientiselite) ||
                          v.partnerName ||
                          "",
                        amount: signedAmount,
                      },
                    ],
                  });
                });
                return;
              }

              const acc = this.getAccountInProject(
                v.projectId,
                v.selectedAccountId,
              );
              const isExpenseTarget =
                (acc &&
                  (acc.type === "expense" ||
                    (acc.type === "investment" &&
                      acc.accountNumber &&
                      String(acc.accountNumber).startsWith("7")))) ||
                isVoucherExpense;

              const finalAmount = isExpenseTarget
                ? -Math.abs(v.totalAmount)
                : v.totalAmount;

              baseTx.amount = finalAmount;
              baseTx.accountId = v.selectedAccountId;
              baseTx.accountName = acc
                ? acc.accountName
                : v.selectedAccountName;
              baseTx.accountNumber = acc
                ? acc.accountNumber
                : v.selectedAccountNumber;
              baseTx.subAccount = acc ? acc.subAccount || "" : "";
              baseTx.items = (v.items || []).map((it) => ({
                ...it,
                amount: isExpenseTarget ? -Math.abs(it.amount) : it.amount,
              }));
            }

            transactionsToSave.push(baseTx);
          }
        });

        // Clean up any stale transactions from previous imports (e.g. single changed to split or vice versa)
        const newTxIds = new Set(transactionsToSave.map((t) => t.id));
        const staleTxIds = [];
        this.vouchers.forEach((v) => {
          const baseId = `${this.year}_${v.tositelaji}_${v.tositenro}`;
          this.existingTransactions.forEach((et) => {
            if (
              et.id &&
              (et.id === baseId || et.id.startsWith(`${baseId}_split_`))
            ) {
              if (!newTxIds.has(et.id)) {
                staleTxIds.push(et.id);
              }
            }
          });
        });

        for (const staleId of staleTxIds) {
          try {
            await deleteTransaction(staleId);
          } catch (delErr) {
            console.warn("Could not delete stale transaction:", staleId, delErr);
          }
        }

        const assignedCount = transactionsToSave.filter(
          (t) => t.status === "assigned",
        ).length;
        const historyMeta = {
          actionType: "CSV_IMPORT",
          summary: `Tuotu ${transactionsToSave.length} tapahtumaa (${
            this.csvFile ? this.csvFile.name : "CSV-raportti"
          })`,
          details: `${assignedCount} kohdistettu tileille, ${
            transactionsToSave.length - assignedCount
          } ohitettu/hylätty.`,
        };

        await saveTransactionsBatch(transactionsToSave, historyMeta);
        this.$toast?.success?.(
          `Tallennettu ${transactionsToSave.length} tapahtumaa onnistuneesti!`,
        );
        this.$emit("imported");
        this.$emit("input", false);
      } catch (err) {
        console.error("Save import error:", err);
        this.$toast?.error?.(
          err.message || "Tapahtumien tallennus epäonnistui",
        );
      } finally {
        this.saving = false;
      }
    },
    getVoucherTypeColor(type) {
      switch (type) {
        case "Ostolasku":
          return "indigo";
        case "Kululasku":
          return "deep-orange";
        case "Tiliote tositteena":
          return "blue-grey";
        case "Viitemaksu":
          return "teal";
        default:
          return "primary";
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "assigned":
          return "green";
        case "declined":
          return "grey";
        default:
          return "orange";
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case "assigned":
          return "Kohdistettu";
        case "declined":
          return "Hylätty";
        default:
          return "Odottaa";
      }
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      return (
        Number(val).toLocaleString("fi-FI", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " €"
      );
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
}
</style>
